import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

declare let gtag: Function;

export interface AnalyticsEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
  [key: string]: any;
}

export interface EcommerceItem {
  item_id: string;
  item_name: string;
  item_category?: string;
  item_category2?: string;
  price?: number;
  quantity?: number;
  currency?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private isInitialized = false;
  private userId: string | null = null;

  constructor(private router: Router) {}

  /**
   * Initialize analytics and start tracking page views
   */
  init(): void {
    if (this.isInitialized) return;
    this.isInitialized = true;

    // Track route changes for SPA navigation
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.trackPageView(event.urlAfterRedirects);
    });

    // Track initial page load
    this.trackPageView(this.router.url);

    // Track web vitals performance
    this.trackWebVitals();

    // Track user engagement
    this.trackEngagement();
  }

  /**
   * Track page views
   */
  trackPageView(url: string, title?: string): void {
    if (typeof gtag === 'undefined') return;

    gtag('event', 'page_view', {
      page_path: url,
      page_title: title || document.title,
      page_location: window.location.href
    });
  }

  /**
   * Set user ID for cross-device tracking
   */
  setUserId(userId: string): void {
    this.userId = userId;
    if (typeof gtag === 'undefined') return;

    gtag('set', { 'user_id': userId });
    gtag('event', 'login', { method: 'email' });
  }

  /**
   * Clear user ID on logout
   */
  clearUserId(): void {
    this.userId = null;
    if (typeof gtag === 'undefined') return;

    gtag('set', { 'user_id': null });
  }

  /**
   * Set user properties for segmentation
   */
  setUserProperties(properties: {
    user_type?: 'free' | 'premium' | 'organizer' | 'trainer';
    subscription_status?: 'active' | 'expired' | 'trial' | 'none';
    company_id?: string;
    language?: string;
    country?: string;
    has_horses?: boolean;
    horse_count?: number;
    membership_count?: number;
  }): void {
    if (typeof gtag === 'undefined') return;

    gtag('set', 'user_properties', properties);
  }

  /**
   * Track custom events
   */
  trackEvent(event: AnalyticsEvent): void {
    if (typeof gtag === 'undefined') return;

    gtag('event', event.action, {
      event_category: event.category,
      event_label: event.label,
      value: event.value,
      ...event
    });
  }

  // ==================== USER ACTIONS ====================

  /**
   * Track user registration
   */
  trackRegistration(method: string = 'email'): void {
    this.trackEvent({
      action: 'sign_up',
      category: 'engagement',
      method
    });
  }

  /**
   * Track user login
   */
  trackLogin(method: string = 'email'): void {
    this.trackEvent({
      action: 'login',
      category: 'engagement',
      method
    });
  }

  /**
   * Track user logout
   */
  trackLogout(): void {
    this.trackEvent({
      action: 'logout',
      category: 'engagement'
    });
    this.clearUserId();
  }

  // ==================== HORSE MANAGEMENT ====================

  /**
   * Track horse added
   */
  trackHorseAdded(horseId: string, horseName: string): void {
    this.trackEvent({
      action: 'horse_added',
      category: 'horses',
      label: horseName,
      horse_id: horseId
    });
  }

  /**
   * Track horse profile view
   */
  trackHorseView(horseId: string, horseName: string): void {
    this.trackEvent({
      action: 'horse_view',
      category: 'horses',
      label: horseName,
      horse_id: horseId
    });
  }

  // ==================== EVENT MANAGEMENT ====================

  /**
   * Track event view
   */
  trackEventView(eventId: number, eventName: string, eventType: string): void {
    this.trackEvent({
      action: 'event_view',
      category: 'events',
      label: eventName,
      event_id: eventId,
      event_type: eventType
    });
  }

  /**
   * Track event registration start
   */
  trackEventRegistrationStart(eventId: number, eventName: string): void {
    this.trackEvent({
      action: 'begin_checkout',
      category: 'events',
      label: eventName,
      event_id: eventId
    });
  }

  /**
   * Track event registration complete
   */
  trackEventRegistrationComplete(eventId: number, eventName: string, amount: number, currency: string = 'NOK'): void {
    if (typeof gtag === 'undefined') return;

    // E-commerce purchase event
    gtag('event', 'purchase', {
      transaction_id: `REG-${eventId}-${Date.now()}`,
      value: amount,
      currency: currency,
      items: [{
        item_id: `event-${eventId}`,
        item_name: eventName,
        item_category: 'Event Registration',
        price: amount,
        quantity: 1
      }]
    });

    this.trackEvent({
      action: 'event_registration_complete',
      category: 'events',
      label: eventName,
      value: amount,
      event_id: eventId
    });
  }

  /**
   * Track class registration
   */
  trackClassRegistration(classId: number, className: string, eventName: string, price: number): void {
    this.trackEvent({
      action: 'class_registration',
      category: 'events',
      label: `${eventName} - ${className}`,
      value: price,
      class_id: classId
    });
  }

  /**
   * Track package purchase
   */
  trackPackagePurchase(packageId: number, packageName: string, price: number, currency: string = 'NOK'): void {
    if (typeof gtag === 'undefined') return;

    gtag('event', 'purchase', {
      transaction_id: `PKG-${packageId}-${Date.now()}`,
      value: price,
      currency: currency,
      items: [{
        item_id: `package-${packageId}`,
        item_name: packageName,
        item_category: 'Training Package',
        price: price,
        quantity: 1
      }]
    });
  }

  // ==================== MEMBERSHIP ====================

  /**
   * Track membership purchase
   */
  trackMembershipPurchase(planId: number, planName: string, organizationName: string, price: number, currency: string = 'NOK'): void {
    if (typeof gtag === 'undefined') return;

    gtag('event', 'purchase', {
      transaction_id: `MEM-${planId}-${Date.now()}`,
      value: price,
      currency: currency,
      items: [{
        item_id: `membership-${planId}`,
        item_name: planName,
        item_category: 'Membership',
        item_category2: organizationName,
        price: price,
        quantity: 1
      }]
    });

    this.trackEvent({
      action: 'membership_purchase',
      category: 'membership',
      label: `${organizationName} - ${planName}`,
      value: price
    });
  }

  // ==================== COMPANY/ORGANIZATION ====================

  /**
   * Track company profile view
   */
  trackCompanyView(companyId: string, companyName: string): void {
    this.trackEvent({
      action: 'company_view',
      category: 'companies',
      label: companyName,
      company_id: companyId
    });
  }

  /**
   * Track event creation
   */
  trackEventCreated(eventId: number, eventName: string, eventType: string): void {
    this.trackEvent({
      action: 'event_created',
      category: 'organizer',
      label: eventName,
      event_id: eventId,
      event_type: eventType
    });
  }

  // ==================== SEARCH & DISCOVERY ====================

  /**
   * Track search
   */
  trackSearch(searchTerm: string, category: string = 'general', resultsCount?: number): void {
    if (typeof gtag === 'undefined') return;

    gtag('event', 'search', {
      search_term: searchTerm,
      search_category: category,
      results_count: resultsCount
    });
  }

  /**
   * Track filter usage
   */
  trackFilterUsed(filterType: string, filterValue: string): void {
    this.trackEvent({
      action: 'filter_used',
      category: 'discovery',
      label: `${filterType}: ${filterValue}`,
      filter_type: filterType,
      filter_value: filterValue
    });
  }

  // ==================== ENGAGEMENT ====================

  /**
   * Track share action
   */
  trackShare(contentType: string, itemId: string, method: string): void {
    if (typeof gtag === 'undefined') return;

    gtag('event', 'share', {
      content_type: contentType,
      item_id: itemId,
      method: method
    });
  }

  /**
   * Track file download
   */
  trackDownload(fileName: string, fileType: string): void {
    this.trackEvent({
      action: 'file_download',
      category: 'engagement',
      label: fileName,
      file_type: fileType
    });
  }

  /**
   * Track outbound link click
   */
  trackOutboundLink(url: string): void {
    this.trackEvent({
      action: 'outbound_link',
      category: 'engagement',
      label: url
    });
  }

  // ==================== ERRORS & EXCEPTIONS ====================

  /**
   * Track error
   */
  trackError(errorMessage: string, fatal: boolean = false): void {
    if (typeof gtag === 'undefined') return;

    gtag('event', 'exception', {
      description: errorMessage,
      fatal: fatal
    });
  }

  // ==================== PERFORMANCE ====================

  /**
   * Track web vitals (Core Web Vitals)
   */
  private trackWebVitals(): void {
    // Track time to interactive
    if ('PerformanceObserver' in window) {
      try {
        // Largest Contentful Paint (LCP)
        const lcpObserver = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries();
          const lastEntry = entries[entries.length - 1];
          this.trackEvent({
            action: 'LCP',
            category: 'Web Vitals',
            value: Math.round((lastEntry as any).startTime),
            non_interaction: true
          });
        });
        lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

        // First Input Delay (FID)
        const fidObserver = new PerformanceObserver((entryList) => {
          const entries = entryList.getEntries();
          entries.forEach((entry: any) => {
            this.trackEvent({
              action: 'FID',
              category: 'Web Vitals',
              value: Math.round(entry.processingStart - entry.startTime),
              non_interaction: true
            });
          });
        });
        fidObserver.observe({ type: 'first-input', buffered: true });

        // Cumulative Layout Shift (CLS)
        let clsValue = 0;
        const clsObserver = new PerformanceObserver((entryList) => {
          for (const entry of entryList.getEntries() as any[]) {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          }
        });
        clsObserver.observe({ type: 'layout-shift', buffered: true });

        // Report CLS on page hide
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'hidden') {
            this.trackEvent({
              action: 'CLS',
              category: 'Web Vitals',
              value: Math.round(clsValue * 1000),
              non_interaction: true
            });
          }
        });
      } catch (e) {
        // PerformanceObserver not fully supported
      }
    }
  }

  /**
   * Track user engagement (time on site, scroll depth)
   */
  private trackEngagement(): void {
    // Track scroll depth
    let maxScrollDepth = 0;
    window.addEventListener('scroll', () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );
      if (scrollPercent > maxScrollDepth) {
        maxScrollDepth = scrollPercent;
        // Track at 25%, 50%, 75%, 90%, 100%
        if ([25, 50, 75, 90, 100].includes(maxScrollDepth)) {
          this.trackEvent({
            action: 'scroll_depth',
            category: 'engagement',
            value: maxScrollDepth,
            non_interaction: true
          });
        }
      }
    }, { passive: true });

    // Reset scroll depth on navigation
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      maxScrollDepth = 0;
    });

    // Track session duration on page hide
    const sessionStart = Date.now();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        const sessionDuration = Math.round((Date.now() - sessionStart) / 1000);
        this.trackEvent({
          action: 'session_duration',
          category: 'engagement',
          value: sessionDuration,
          non_interaction: true
        });
      }
    });
  }

  // ==================== E-COMMERCE HELPERS ====================

  /**
   * Track add to cart (for registration flow)
   */
  trackAddToCart(items: EcommerceItem[], currency: string = 'NOK'): void {
    if (typeof gtag === 'undefined') return;

    const value = items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);

    gtag('event', 'add_to_cart', {
      currency: currency,
      value: value,
      items: items
    });
  }

  /**
   * Track view item (for event/class details)
   */
  trackViewItem(item: EcommerceItem, currency: string = 'NOK'): void {
    if (typeof gtag === 'undefined') return;

    gtag('event', 'view_item', {
      currency: currency,
      value: item.price || 0,
      items: [item]
    });
  }

  /**
   * Track begin checkout
   */
  trackBeginCheckout(items: EcommerceItem[], currency: string = 'NOK'): void {
    if (typeof gtag === 'undefined') return;

    const value = items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);

    gtag('event', 'begin_checkout', {
      currency: currency,
      value: value,
      items: items
    });
  }
}
